import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LineChart, TrendingUp, Server, AlertOctagon, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart as ReLine, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell, PieChart, Pie, Legend,
} from "recharts";

const RANGES = [
  { days: 1,  label: "24h" },
  { days: 7,  label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];
const CAT_COLORS = { breakdown: "#dc2626", changeover: "#f59e0b", material: "#8b5cf6", quality: "#0ea5e9", planned: "#64748b", other: "#94a3b8" };

function StatCard({ label, value, suffix = "", accent }) {
  return (
    <div className="border border-border bg-card rounded-sm p-5">
      <div className="overline text-muted-foreground">{label}</div>
      <div className={`font-display font-black text-4xl mt-2 tabular-nums ${accent || ""}`}>
        {value}{suffix}
      </div>
    </div>
  );
}

function Pct({ value }) {
  const v = Number(value || 0);
  const color = v >= 85 ? "bg-emerald-500" : v >= 60 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-secondary rounded-sm overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, v)}%` }} />
      </div>
      <span className="text-xs font-mono-plex tabular-nums w-10 text-right">{v.toFixed(0)}%</span>
    </div>
  );
}

export default function GmesOee() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/gmes/oee/summary?days=${days}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/gmes/oee/report.pdf?days=${days}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `oee-report-${days}d-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (err) {
      toast.error("Could not generate report");
    } finally {
      setDownloading(false);
    }
  };

  const avg = data?.avg || {};
  const totals = data?.totals || {};
  const perMachine = data?.per_machine || [];
  const daily = data?.daily_trend || [];
  const cats = data?.downtime_by_category || [];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
            <Link to="/app/shopfloor" className="hover:text-primary">Shop Floor</Link> / OEE
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-2" data-testid="gmes-oee-title">
            OEE Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Overall Equipment Effectiveness = Availability × Performance × Quality.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={downloadPdf} disabled={downloading || loading} variant="outline" className="rounded-sm h-9" data-testid="oee-download-pdf-btn">
            <Download className="w-4 h-4 mr-2" />
            {downloading ? "Preparing…" : "Download PDF"}
          </Button>
          <div className="flex gap-1 border border-border rounded-sm p-1 bg-card">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                className={`px-3 py-1.5 text-xs font-mono-plex uppercase tracking-widest rounded-sm ${
                  days === r.days ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary"
                }`}
                data-testid={`oee-range-${r.days}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="border border-dashed border-border rounded-sm p-16 text-center text-muted-foreground">Loading OEE…</div>
      ) : perMachine.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-16 text-center bg-card/40">
          <LineChart className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="font-display text-lg mt-4">No machines to measure yet</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Add a machine and start logging downtime and production to see live OEE here.
          </p>
          <Link to="/app/shopfloor/machines" className="inline-block mt-4 text-primary font-mono-plex uppercase tracking-widest text-xs hover:underline">
            → Add a machine
          </Link>
        </div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatCard label="OEE" value={avg.oee_pct ?? 0} suffix="%" accent={avg.oee_pct >= 85 ? "text-emerald-600" : avg.oee_pct >= 60 ? "text-amber-600" : "text-red-600"} />
            <StatCard label="Availability" value={avg.availability_pct ?? 0} suffix="%" />
            <StatCard label="Performance" value={avg.performance_pct ?? 0} suffix="%" />
            <StatCard label="Quality" value={avg.quality_pct ?? 0} suffix="%" />
          </div>

          {/* Charts row */}
          <div className="grid lg:grid-cols-3 gap-4 mb-6">
            <div className="lg:col-span-2 border border-border bg-card rounded-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="overline text-muted-foreground">Daily output</div>
                  <div className="font-display font-bold text-lg mt-1">Units produced vs scrap</div>
                </div>
                <TrendingUp className="w-4 h-4 text-muted-foreground" />
              </div>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <ReLine data={daily} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                    <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
                    <Line type="monotone" dataKey="units" stroke="#10b981" strokeWidth={2} dot={false} name="Units" />
                    <Line type="monotone" dataKey="scrap" stroke="#dc2626" strokeWidth={2} dot={false} name="Scrap" />
                  </ReLine>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="border border-border bg-card rounded-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="overline text-muted-foreground">Downtime</div>
                  <div className="font-display font-bold text-lg mt-1">By reason</div>
                </div>
                <AlertOctagon className="w-4 h-4 text-muted-foreground" />
              </div>
              {cats.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-16">No downtime yet</div>
              ) : (
                <div style={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={cats} dataKey="minutes" nameKey="category" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                        {cats.map((c, i) => (
                          <Cell key={i} fill={CAT_COLORS[c.category] || "#94a3b8"} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `${v} min`} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* OEE bars per machine */}
          <div className="border border-border bg-card rounded-sm p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline text-muted-foreground">Per machine</div>
                <div className="font-display font-bold text-lg mt-1">OEE ranking</div>
              </div>
              <Server className="w-4 h-4 text-muted-foreground" />
            </div>
            <div style={{ width: "100%", height: Math.max(200, perMachine.length * 42) }}>
              <ResponsiveContainer>
                <BarChart data={perMachine} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 5 }}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis type="category" dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} width={120} />
                  <Tooltip formatter={(v) => `${v}%`} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
                  <Bar dataKey="oee_pct" name="OEE" fill="#0ea5e9">
                    {perMachine.map((m, i) => (
                      <Cell key={i} fill={m.oee_pct >= 85 ? "#10b981" : m.oee_pct >= 60 ? "#f59e0b" : "#dc2626"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Detail table */}
          <div className="border border-border bg-card rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50">
                <tr className="text-left">
                  {["Machine", "Status", "OEE", "Availability", "Performance", "Quality", "Units", "Scrap", "Downtime"].map((h) => (
                    <th key={h} className="px-3 py-2 font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {perMachine.map((m) => (
                  <tr key={m.machine_id} className="border-t border-border" data-testid={`oee-row-${m.machine_id}`}>
                    <td className="px-3 py-2 font-medium">{m.name}</td>
                    <td className="px-3 py-2 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">{m.status}</td>
                    <td className="px-3 py-2 w-40"><Pct value={m.oee_pct} /></td>
                    <td className="px-3 py-2 w-40"><Pct value={m.availability_pct} /></td>
                    <td className="px-3 py-2 w-40"><Pct value={m.performance_pct} /></td>
                    <td className="px-3 py-2 w-40"><Pct value={m.quality_pct} /></td>
                    <td className="px-3 py-2 tabular-nums">{m.units_produced}</td>
                    <td className="px-3 py-2 tabular-nums text-red-600">{m.scrap}</td>
                    <td className="px-3 py-2 tabular-nums text-xs">{m.downtime_minutes} min</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[11px] text-muted-foreground mt-4 max-w-3xl">
            Availability assumes a 24/7 planned schedule (customisable in a future release). Performance uses your machine&rsquo;s <em>ideal cycle</em> setting — set it under Machines to enable this metric.
          </p>
        </>
      )}
    </div>
  );
}
